import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { ISubscriptionPlan } from '../../redux/subscription/types';
import {
    getCreatorSubscriptionProvider,
    canReactivatePaystackCreatorSubscription,
} from '../../utils/creatorSubscriptionApi';
import {
    creatorSubscriptionIntervalLabel,
    formatCreatorSubscriptionDate,
    formatCreatorSubscriptionPrice,
    resolveCreatorSubscriptionStartDate,
} from '../../utils/creatorSubscriptionFormat';
import {
    CREATOR_SUBSCRIPTION_MANAGE_COPY,
    getCreatorCancelConfirmCopy,
    getCreatorCancelPremiumImpactLead,
    getCreatorUpdatePaymentButtonLabel,
} from '../../constants/creatorSubscriptionManageCopy';
import { CreatorSubscriptionFeatureList } from './CreatorSubscriptionFeatureList';
import type { IPremiumCampaignImpactCampaign } from '../../redux/subscription/types';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../analytics/Mixpanel';

const LUXE_PRIMARY = '#ffc965';
const LUXE_SECONDARY = '#4caf50';
const SURFACE = '#1a1a1a';
const OUTLINE = 'rgba(255,255,255,0.12)';

const subscriptionDialogPaperSx = {
    bgcolor: '#141414',
    backgroundImage: 'none',
    borderRadius: 3,
    border: `1px solid ${OUTLINE}`,
};

export interface CreatorSubscriptionManageModalProps {
    open: boolean;
    onClose: () => void;
    subscription: ISubscriptionPlan;
    catalogPlans?: ISubscriptionPlan[];
    cancelling?: boolean;
    updatingPayment?: boolean;
    reactivating?: boolean;
    onUpdatePayment: () => void;
    onCancel: () => void;
    onReactivate?: () => void;
    premiumCampaignsAtRisk?: IPremiumCampaignImpactCampaign[];
}

export function CreatorSubscriptionManageModal({
    open,
    onClose,
    subscription,
    catalogPlans = [],
    cancelling = false,
    updatingPayment = false,
    reactivating = false,
    onUpdatePayment,
    onCancel,
    onReactivate,
    premiumCampaignsAtRisk = [],
}: CreatorSubscriptionManageModalProps) {
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const prevCancelling = React.useRef(false);
    const provider = getCreatorSubscriptionProvider(subscription);
    const showReactivate = canReactivatePaystackCreatorSubscription(subscription);
    const busy = cancelling || updatingPayment || reactivating;

    React.useEffect(() => {
        if (prevCancelling.current && !cancelling) {
            setCancelConfirmOpen(false);
            onClose();
        }
        prevCancelling.current = !!cancelling;
    }, [cancelling, onClose]);

    React.useEffect(() => {
        if (!open) setCancelConfirmOpen(false);
    }, [open]);

    const planName = subscription.plan_name ?? subscription.name ?? 'Your Plan';
    const planDescription = subscription.description?.trim() || 'Creator membership';
    const statusLabel = (subscription.status ?? 'active').replace(/-/g, ' ').toUpperCase();
    const isActiveStatus = subscription.status === 'active' || subscription.status === 'trialing';

    const features = useMemo(() => {
        if (subscription.features?.length) return subscription.features;
        const code = subscription.plan?.plan_code ?? subscription.plan_code;
        const matched = catalogPlans.find(p => p.plan_code === code);
        return matched?.features ?? [];
    }, [catalogPlans, subscription]);

    const premiumImpactLead = useMemo(
        () => getCreatorCancelPremiumImpactLead(premiumCampaignsAtRisk.map(c => c.title)),
        [premiumCampaignsAtRisk],
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={() => !busy && onClose()}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { ...subscriptionDialogPaperSx, maxWidth: 560 } }}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        color: '#fff',
                        pb: 1,
                    }}>
                    Your subscription
                    <IconButton onClick={onClose} disabled={busy} size="small" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            p: 3,
                            borderRadius: 3,
                            bgcolor: SURFACE,
                            border: `1px solid ${LUXE_PRIMARY}33`,
                            overflow: 'hidden',
                            mb: features.length ? 3 : 0,
                        }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -60,
                                right: -60,
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                bgcolor: `${LUXE_PRIMARY}14`,
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2.5,
                                gap: 2,
                            }}>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.5,
                                        mb: 1,
                                        borderRadius: 99,
                                        bgcolor: `${isActiveStatus ? LUXE_SECONDARY : LUXE_PRIMARY}18`,
                                        border: `1px solid ${isActiveStatus ? LUXE_SECONDARY : LUXE_PRIMARY}33`,
                                    }}>
                                    <VerifiedIcon
                                        sx={{ fontSize: 14, color: isActiveStatus ? LUXE_SECONDARY : LUXE_PRIMARY }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: isActiveStatus ? LUXE_SECONDARY : LUXE_PRIMARY,
                                            letterSpacing: '0.1em',
                                        }}>
                                        {statusLabel}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 24,
                                        fontWeight: 800,
                                        color: '#fff',
                                        lineHeight: 1.1,
                                    }}>
                                    {planName}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.55)', mt: 0.5, fontSize: 14 }}>
                                    {planDescription}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: LUXE_PRIMARY }}>
                                    {formatCreatorSubscriptionPrice(subscription)}
                                </Typography>
                                <Typography
                                    sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.06em' }}>
                                    {creatorSubscriptionIntervalLabel(subscription)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.55)' }}>
                                <CalendarTodayIcon sx={{ fontSize: 16, color: LUXE_PRIMARY }} />
                                <Typography sx={{ fontSize: 13 }}>
                                    Subscribed{' '}
                                    {formatCreatorSubscriptionDate(resolveCreatorSubscriptionStartDate(subscription))}
                                </Typography>
                            </Box>
                            {subscription.next_payment_date && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: 'rgba(255,255,255,0.55)',
                                    }}>
                                    <EventIcon sx={{ fontSize: 16, color: LUXE_PRIMARY }} />
                                    <Typography sx={{ fontSize: 13 }}>
                                        Next payment {formatCreatorSubscriptionDate(subscription.next_payment_date)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Button
                            onClick={onUpdatePayment}
                            disabled={busy || !provider}
                            startIcon={
                                updatingPayment ? <CircularProgress size={14} sx={{ color: '#392700' }} /> : null
                            }
                            sx={{
                                py: 1,
                                px: 3,
                                borderRadius: 99,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 700,
                                textTransform: 'none',
                                color: '#392700',
                                background: `linear-gradient(135deg, ${LUXE_PRIMARY} 0%, #feb700 100%)`,
                            }}>
                            {updatingPayment ? 'Updating…' : getCreatorUpdatePaymentButtonLabel(provider)}
                        </Button>
                    </Box>

                    {features.length > 0 && (
                        <>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 15,
                                    mb: 1.5,
                                    color: '#fff',
                                }}>
                                Included with {planName}
                            </Typography>
                            <CreatorSubscriptionFeatureList features={features} highlightColor={LUXE_PRIMARY} />
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        pt: 0,
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1.5,
                    }}>
                    {showReactivate && onReactivate && (
                        <Button
                            fullWidth
                            onClick={() => {
                                trackSubscriptionAction(MIXPANEL_EVENT.REACTIVATE_SUBSCRIPTION_CLICK, {
                                    buttonName: 'REACTIVATE SUBSCRIPTION',
                                    buttonAction: 'REACTIVATE_SUBSCRIPTION',
                                    subscriptionType: 'creator',
                                });
                                onReactivate();
                            }}
                            disabled={busy}
                            startIcon={
                                reactivating ? <CircularProgress size={14} sx={{ color: LUXE_PRIMARY }} /> : null
                            }
                            sx={{
                                color: LUXE_PRIMARY,
                                textTransform: 'none',
                                fontSize: 14,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                border: `1px solid ${LUXE_PRIMARY}44`,
                                borderRadius: 99,
                                py: 1.25,
                            }}>
                            {reactivating ? 'Reactivating…' : CREATOR_SUBSCRIPTION_MANAGE_COPY.reactivateButton}
                        </Button>
                    )}
                    <Button
                        fullWidth
                        onClick={() => {
                            trackSubscriptionAction(MIXPANEL_EVENT.CANCEL_SUBSCRIPTION_CLICK, {
                                buttonName: 'CANCEL SUBSCRIPTION',
                                buttonAction: 'CANCEL_SUBSCRIPTION',
                                subscriptionType: 'creator',
                            });
                            setCancelConfirmOpen(true);
                        }}
                        disabled={busy}
                        sx={{
                            color: '#ff4d4f',
                            textTransform: 'none',
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            border: '1px solid rgba(255,77,79,0.25)',
                            borderRadius: 99,
                            py: 1.25,
                        }}>
                        Cancel Subscription
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelConfirmOpen}
                onClose={() => !busy && setCancelConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: subscriptionDialogPaperSx }}>
                <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', pb: 1 }}>
                    Cancel subscription?
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {premiumImpactLead ? (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                color: '#ff8a80',
                                lineHeight: 1.55,
                                mb: 1.5,
                            }}>
                            {premiumImpactLead}
                        </Typography>
                    ) : null}
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.75)',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                        }}>
                        {getCreatorCancelConfirmCopy(provider)}
                    </Typography>
                    {premiumCampaignsAtRisk.length > 0 ? (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,77,79,0.08)',
                                border: '1px solid rgba(255,77,79,0.25)',
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: '#ff4d4f',
                                    mb: premiumCampaignsAtRisk.length > 1 ? 1 : 0,
                                }}>
                                {CREATOR_SUBSCRIPTION_MANAGE_COPY.cancelPremiumCampaignWarning}
                            </Typography>
                            {premiumCampaignsAtRisk.length > 1 ? (
                                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                                    {premiumCampaignsAtRisk.map(c => (
                                        <Typography
                                            component="li"
                                            key={c.participation_id || c.campaign_id}
                                            sx={{
                                                fontSize: 13,
                                                color: 'rgba(255,255,255,0.85)',
                                                mb: 0.5,
                                            }}>
                                            {c.title}
                                        </Typography>
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => {
                            trackSubscriptionAction(MIXPANEL_EVENT.KEEP_SUBSCRIPTION_CLICK, {
                                buttonName: 'KEEP PLAN',
                                buttonAction: 'KEEP_SUBSCRIPTION',
                                subscriptionType: 'creator',
                            });
                            setCancelConfirmOpen(false);
                        }}
                        disabled={busy}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: 'rgba(255,255,255,0.6)',
                        }}>
                        Keep plan
                    </Button>
                    <Button
                        onClick={() => {
                            trackSubscriptionAction(MIXPANEL_EVENT.PROCEED_WITH_CANCEL_SUBSCRIPTION, {
                                buttonName: 'YES CANCEL',
                                buttonAction: 'PROCEED_WITH_CANCEL_SUBSCRIPTION',
                                subscriptionType: 'creator',
                            });
                            onCancel();
                        }}
                        disabled={busy}
                        startIcon={cancelling ? <CircularProgress size={14} sx={{ color: '#ff4d4f' }} /> : null}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: '#ff4d4f',
                            minWidth: 120,
                        }}>
                        {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
